import { useMemo, useEffect } from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Container,
  Flex,
  Heading,
  Stack,
  VStack,
  Text,
  HStack,
  Tag,
  Button,
  TagLabel,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { CopyIcon } from "@chakra-ui/icons";
import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import InfoOutlined from "@material-ui/icons/InfoOutlined";

import { ReactComponent as ReferralIllustration } from "assets/svg/referral-illustration.svg";

import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";

import { ReactComponent as FacebookReferIcon } from "assets/svg/facebook-refer.svg";
import { ReactComponent as TwitterReferIcon } from "assets/svg/twitter-refer.svg";
import { ReactComponent as WhatsappReferIcon } from "assets/svg/whatsapp-refer.svg";
import { ReactComponent as CopyReferIcon } from "assets/svg/copy-refer.svg";

import JoinIcon from "assets/svg/join-affiliate.svg";
import ViewIcon from "assets/svg/view-affiliate.svg";
import ResourcesIcon from "assets/svg/resources.svg";
import RulesIcon from "assets/svg/rules-and-regulations.svg";
import LeaderboardIcon from "assets/png/leaderboard.png";

//assets

// components
import Header from "components/dashHeader";
import Sidemenu from "components/sidemenu";
// import Button from "components/Button/Button";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";
import VerifyAccountNotice from "components/Modals/cards/VerifyAccountNotice";

import { useJoinAffiliateMutation } from "services/walletApi";

// import { useIntercom } from "react-use-intercom";
// import ProfileShortInfo from "components/ProfileShortInfo/ProfileShortInfo";
import AccountSidebar from "components/AccountSidebar/AccountSidebar";
import AccountLink from "components/AccountLink/AccountLink";

import useCopy from "hooks/useCopy";

import getError from "util/getError";
import { isProd, isDev } from "config/constant";

export default function Affiliates(props) {
  const { userData, refetchProfile } = props;

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    joinAffiliate,
    { data: joinData, isLoading: isJoining, isSuccess, error, isError },
  ] = useJoinAffiliateMutation();

  // console.log("USER DATA", userData);

  const isEligible = useMemo(() => userData?.kycLevel === "three", [userData]);

  useEffect(() => {
    if (!isEligible) {
      onOpen();
    }
  }, [isEligible, onOpen]);

  const appHost = useMemo(() => {
    if (isProd) return "https://dashboard.bitmama.io";
    if (isDev) return "https://bitmama-staging.web.app";
  }, []);

  const { onCopy, value } = useCopy(userData?.refCode);
  const { onCopy: onLinkCopy, value: linkValue } = useCopy(
    `${appHost}/signup?refCode=${userData?.refCode}`
  );

  const handleJoin = () => {
    joinAffiliate({});
  };

  useEffect(() => {
    if (isSuccess && joinData) {
      // onOpen();
      refetchProfile();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [error, joinData, isError, onOpen, toast, refetchProfile, isSuccess]);

  // eslint-disable-next-line
  const links = [
    {
      name: "Join Partners Program",
      id: "join",
      link: "/dashboard/account/partners",
      icon: JoinIcon,
      onClick: () => handleJoin(),
    },
    {
      name: "View Partners Program",
      id: "view",
      link: "/dashboard/account/partners/view",
      icon: ViewIcon,
    },

    {
      name: "Resources",
      id: "resources",
      link: "/dashboard/account/partners/resources",
      icon: ResourcesIcon,
      onClick: () =>
        window.open(
          "https://drive.google.com/drive/folders/1_owkrCizRH9ip5Ra9Xw70UN4NyUJBRgy?usp=sharing",
          "blank"
        ),
    },
    {
      name: "Rules and Regulations",
      id: "rules",
      link: "/dashboard/account/partners/rules",
      icon: RulesIcon,
    },
    {
      name: "Leaderboard",
      id: "leaderboard",
      link: "/dashboard/account/partners/leaderboard",
      icon: LeaderboardIcon,
    },
  ];

  return (
    <Box>
      <Sidemenu currentPage="Account" logoutUser={props.logout} />
      <Header />
      <Box as="section">
        <div className="account-container">
          <Flex className="flex">
            <Box flex=".8" borderRight="1px solid #D9DBE9" position="sticky">
              <Container maxW="calc(100% - 2em)" ml="10px">
                <Stack mb="48px">
                  <Heading>Account</Heading>
                  <Breadcrumb
                    className="animate__animated animate__fadeInDown"
                    separator={
                      <ArrowForwardIosRoundedIcon
                        style={{ fontSize: ".8em", color: "#6E7191" }}
                      />
                    }
                  >
                    <BreadcrumbItem>
                      <BreadcrumbLink to="/dashboard/account">
                        Account
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                      <BreadcrumbLink isCurrentPage to="">
                        Bitmama Partners
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </Breadcrumb>
                </Stack>

                <AccountSidebar
                  current="affiliates"
                  userData={userData}
                  logout={props.logout}
                />
              </Container>
            </Box>

            <Box flex="1" mt={{ sm: "30px", lg: "60px" }}>
              <Container
                maxW={["md"]}
                ml={{ lg: "60px" }}
                className="container"
              >
                <Stack gridGap="20px" padding={"1rem"}>
                  <VStack>
                    <VStack>
                      <Heading as="h4" fontSize="xl" mb="40px">
                        Bitmama Partners
                      </Heading>
                      <Box as={ReferralIllustration} />

                      <Text
                        color="var(--primary)"
                        fontWeight="600"
                        fontSize="18px"
                        mt="4px !important"
                      >
                        We Earn, You Earn
                      </Text>
                      <Text fontSize="sm" mt="4px !important">
                        When all referred users completes a trade.{" "}
                      </Text>
                      <HStack mt="4px !important">
                        <Text fontSize="sm" color="#6E7191" fontWeight="600">
                          All T&Cs apply
                        </Text>
                        <Box
                          sx={{ "font-size": "16px !important" }}
                          as={InfoOutlined}
                        />
                      </HStack>
                    </VStack>

                    <VStack mt="20px !important">
                      <Tag
                        size="sm"
                        // key={size}
                        variant="subtle"
                        bg="#EFF0F6"
                        p="12px 18px"
                        borderRadius="100px"
                        minW="65%"
                        w="100%"
                        justifyContent="space-between"
                      >
                        <TagLabel
                          ml="5px"
                          fontWeight="400"
                          fontSize="md"
                          textTransform="uppercase"
                          color="#6E7191"
                        >
                          {value}
                        </TagLabel>
                        <Button
                          size="xs"
                          variant="ghost"
                          aria-label="copy"
                          _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
                          // icon={}
                          onClick={onCopy}
                        >
                          <HStack color="var(--primary)">
                            <CopyIcon fontSize="18px" />
                            <Text
                              ml="4px !important"
                              fontSize="sm"
                              fontWeight="400"
                            >
                              Copy code
                            </Text>
                          </HStack>
                        </Button>
                      </Tag>

                      <Text fontSize="xs" color="#6E7191" fontWeight="400">
                        Or Share via
                      </Text>
                      <HStack gridGap="18px" justifyContent="center">
                        <FacebookShareButton
                          url={linkValue}
                          hashtag={"#hashtag"}
                          description={"aiueo"}
                        >
                          <VStack>
                            <FacebookReferIcon />
                            <Text color="#4E4B66" fontSize="xs">
                              Facebook
                            </Text>
                          </VStack>
                        </FacebookShareButton>

                        <TwitterShareButton
                          url={linkValue}
                          hashtag={"#hashtag"}
                          description={"aiueo"}
                        >
                          <VStack>
                            <TwitterReferIcon />
                            <Text color="#4E4B66" fontSize="xs">
                              Twitter
                            </Text>
                          </VStack>
                        </TwitterShareButton>

                        <WhatsappShareButton
                          url={linkValue}
                          hashtag={"#hashtag"}
                          description={"aiueo"}
                        >
                          <VStack>
                            <WhatsappReferIcon />
                            <Text color="#4E4B66" fontSize="xs">
                              Whatsapp
                            </Text>
                          </VStack>
                        </WhatsappShareButton>

                        <Button
                          // url={"https://peing.net/ja/"}
                          // hashtag={"#hashtag"}
                          // description={"aiueo"}
                          minH="fit-content"
                          h="fit-content"
                          minW="fit-content"
                          w="fit-content"
                          p="0"
                          bg="transparent"
                          _hover={{ bg: "transparent" }}
                          _active={{ bg: "transparent" }}
                          _focus={{ bg: "transparent" }}
                          onClick={onLinkCopy}
                        >
                          <VStack>
                            <CopyReferIcon />
                            <Text
                              color="#4E4B66"
                              fontSize="xs"
                              fontWeight="400"
                            >
                              Copy link
                            </Text>
                          </VStack>
                        </Button>
                      </HStack>
                    </VStack>
                  </VStack>
                </Stack>

                <Stack gridGap="20px" padding={"1rem"}>
                  <Stack gridGap="4px">
                    {links.map((item, i) => {
                      if (userData?.affiliatePartner && item.id !== "join") {
                        return (
                          <AccountLink
                            key={`account-link-${i}`}
                            to={item.link}
                            icon={item.icon}
                            onClick={item?.onClick}
                            // current={item.id === current}
                          >
                            {item.name}
                          </AccountLink>
                        );
                      } else if (
                        !userData?.affiliatePartner &&
                        item.id !== "view"
                      ) {
                        return (
                          <AccountLink
                            key={`account-link-${i}`}
                            to={item.link}
                            icon={item.icon}
                            onClick={item?.onClick}
                            isLoading={item.id === "join" && isJoining}
                            // current={item.id === current}
                          >
                            {item.name}
                          </AccountLink>
                        );
                      }

                      return <></>;
                    })}
                  </Stack>
                </Stack>
              </Container>
            </Box>
          </Flex>
        </div>
      </Box>

      <VerifyAccountNotice isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}
